import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {
  BrowserRouter,
  Navigate,
  matchPath,
  useInRouterContext,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import {history} from '../store/store';

export const Redirect = ({to, push = false}) => {
  const {state, ...target} = typeof to === 'object' && to !== null ? to : {};
  return <Navigate to={state ? target : to} state={state} replace={!push} />;
};

Redirect.propTypes = {
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  push: PropTypes.bool,
};

export const OptionalBrowserRouter = ({children}) => {
  const inRouter = useInRouterContext();
  return inRouter ? children : <BrowserRouter>{children}</BrowserRouter>;
};

OptionalBrowserRouter.propTypes = {
  children: PropTypes.node.isRequired,
};

const createRouteHistory = (navigate, location) => ({
  ...history,
  location,
  push: (to, state) => navigate(to, {state}),
  replace: (to, state) => navigate(to, {replace: true, state}),
});

export const useHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return useMemo(() => createRouteHistory(navigate, location), [navigate, location]);
};

export const withRouter = (WrappedComponent) => {
  const RouterWrapper = (props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const routeHistory = useMemo(() => createRouteHistory(navigate, location), [navigate, location]);

    const match = {
      params,
      path: location.pathname,
      url: location.pathname,
      isExact: true,
    };

    return (
      <WrappedComponent
        {...props}
        history={routeHistory}
        location={location}
        match={match}
      />
    );
  };

  RouterWrapper.displayName = `withRouter(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return RouterWrapper;
};

export {matchPath};
